import { isPlatformBrowser } from '@angular/common';
import { AfterViewInit, Component, ChangeDetectionStrategy, OnDestroy, OnInit, PLATFORM_ID, inject, signal } from '@angular/core';
import { RouterLink } from '@angular/router';
import { AppIcon } from './icon';
import { TestimonialResponse, TestimonialsService } from '../testimonials/testimonials.service';
import { environment } from '../../../environments/environment';

interface CalculatorCard {
  /** Key into HOME_ICON_PATHS, e.g. 'home' */
  icon: string;
  title: string;
  description: string;
  route: string;
}

interface CalculatorGroup {
  label: string;
  hint: string;
  calculators: CalculatorCard[];
}

interface MoneyInsight {
  /** Key into HOME_ICON_PATHS, e.g. 'home' */
  icon: string;
  title: string;
  body: string;
}

@Component({
  selector: 'app-home',
  standalone: true,
  imports: [RouterLink, AppIcon],
  templateUrl: './home.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./home.scss']
})
export class Home implements OnInit, AfterViewInit, OnDestroy {
  private readonly testimonialsService = inject(TestimonialsService);
  private readonly isBrowser: boolean;
  private scrollHandler?: () => void;
  private tickingScroll = false;

  readonly testimonials = signal<TestimonialResponse[]>([]);
  readonly stars = [1, 2, 3, 4, 5];
  /** Gated behind app.routes.ts's own /rms gate - see PENDING_FEATURES.md #1. */
  readonly rmsEnabled = environment.features.rmsEnabled;
  /** Gated behind app.routes.ts's own /rental-agreement gate - see PENDING_FEATURES.md #3. */
  readonly rentalAgreementEnabled = environment.features.rentalAgreementEnabled;
  /** Which hero CTA should read as "selected" — tracks the section scrolled into view. */
  readonly activeHeroSection = signal<'calculators' | 'services'>('calculators');

  constructor() {
    const platformId = inject(PLATFORM_ID);

    this.isBrowser = isPlatformBrowser(platformId);
  }

  ngOnInit(): void {
    // Prerendered at build time with no SSR API base URL configured — fetch client-side only,
    // same as the /testimonials page. During prerender this section simply renders empty.
    if (!this.isBrowser) {
      return;
    }
    this.testimonialsService.list().subscribe({
      next: (list) => this.testimonials.set(list.slice(0, 3)),
      error: () => this.testimonials.set([]),
    });
  }

  ngAfterViewInit(): void {
    if (!this.isBrowser) {
      return;
    }
    this.updateActiveHeroSection();
    this.scrollHandler = () => {
      if (this.tickingScroll) {
        return;
      }
      this.tickingScroll = true;
      requestAnimationFrame(() => {
        this.updateActiveHeroSection();
        this.tickingScroll = false;
      });
    };
    window.addEventListener('scroll', this.scrollHandler, { passive: true });
  }

  ngOnDestroy(): void {
    if (this.scrollHandler) {
      window.removeEventListener('scroll', this.scrollHandler);
    }
  }

  // Reads live DOM positions rather than tracking scroll direction, so it's correct
  // even after an instant jump (e.g. clicking a hero CTA, not just gradual scrolling).
  private updateActiveHeroSection(): void {
    const servicesEl = document.getElementById('services');
    if (!servicesEl) {
      return;
    }
    const viewportMid = window.innerHeight / 2;
    const servicesTop = servicesEl.getBoundingClientRect().top;
    this.activeHeroSection.set(servicesTop <= viewportMid ? 'services' : 'calculators');
  }

  readonly insights: MoneyInsight[] = [
    {
      icon: 'report_problem',
      title: 'One mistake can cost you lakhs',
      body: 'The wrong tenure, an uncompared interest rate, or a prepayment opportunity you never noticed — small oversights like these quietly add lakhs to what you end up repaying. A two-minute calculation catches it before you sign anything.'
    },
    {
      icon: 'savings',
      title: 'One prepayment can save a fortune',
      body: 'Put an extra ₹1 lakh toward your home loan in year two, and you can save several lakhs in interest and cut years off your tenure — because it strikes the principal while the most interest is still ahead of you.'
    },
    {
      icon: 'trending_up',
      title: 'Small, regular amounts outgrow big, late ones',
      body: 'A modest SIP started today does more work than a larger one started three years from now. Compounding rewards time far more than it rewards the size of any single investment.'
    }
  ];

  readonly groups: CalculatorGroup[] = [
    {
      label: 'Home Loan Calculators',
      hint: 'Everything tied to buying, financing and selling a home — in one place.',
      calculators: [
        {
          icon: 'home',
          title: 'Home Loan EMI Calculator',
          description: 'EMI, full amortization schedule, prepayment planning and an AI advisor for your home loan.',
          route: '/home-loan'
        },
        {
          icon: 'payments',
          title: 'Home Loan Prepayment Calculator',
          description: 'See how much interest you save and how many years you cut off with a lump-sum or extra monthly payment.',
          route: '/home-loan-prepayment'
        },
        {
          icon: 'location_city',
          title: 'Karnataka Stamp Duty Calculator',
          description: 'Estimate stamp duty, cess, surcharge and registration charges on a property sale in Karnataka.',
          route: '/stamp-duty-karnataka'
        },
        {
          icon: 'home_work',
          title: 'TDS on Property Sale Calculator (Form 141/26QB)',
          description: 'Calculate TDS on a property sale under Section 393(1)/393(2) of the Income-tax Act, 2025, plus state-wise stamp duty and late fee/interest.',
          route: '/tds-property-sale'
        }
      ]
    },
    {
      label: 'Loan Calculators',
      hint: 'See the true cost of borrowing before you commit — and how to cut it down.',
      calculators: [
        {
          icon: 'credit_card',
          title: 'Personal Loan EMI Calculator',
          description: 'Calculate EMI, total interest and the full repayment schedule for a personal loan.',
          route: '/personal-loan'
        },
        {
          icon: 'directions_car',
          title: 'Car Loan EMI Calculator',
          description: 'Calculate EMI, total interest and the full repayment schedule for a car loan.',
          route: '/car-loan'
        }
      ]
    },
    {
      label: 'Investment & Savings Calculators',
      hint: 'Turn small, regular amounts into long-term wealth — and plan the withdrawals too.',
      calculators: [
        {
          icon: 'account_balance',
          title: 'PPF Calculator',
          description: 'Estimate your Public Provident Fund maturity value and year-wise interest growth.',
          route: '/ppf'
        },
        {
          icon: 'child_care',
          title: 'Sukanya Samriddhi Yojana Calculator',
          description: 'Estimate the maturity value of an SSY account opened for your girl child.',
          route: '/ssy'
        },
        {
          icon: 'savings',
          title: 'FD Calculator',
          description: 'Calculate the maturity value and interest earned on a bank Fixed Deposit.',
          route: '/fd'
        },
        {
          icon: 'autorenew',
          title: 'RD Calculator',
          description: 'Calculate the maturity value and interest earned on a bank Recurring Deposit.',
          route: '/rd'
        },
        {
          icon: 'percent',
          title: 'CAGR Calculator',
          description: 'Find the compound annual growth rate between an initial and final investment value.',
          route: '/cagr'
        },
        {
          icon: 'price_change',
          title: 'Inflation Calculator',
          description: 'See the future cost of an item, or how much today\'s money will be worth after inflation.',
          route: '/inflation'
        },
        {
          icon: 'apartment',
          title: 'EPF Calculator',
          description: 'Project your Employee Provident Fund balance at retirement, with employee and employer contributions.',
          route: '/epf'
        },
        {
          icon: 'elderly',
          title: 'NPS Calculator',
          description: 'Project your National Pension System corpus, lumpsum withdrawal and monthly pension at retirement.',
          route: '/nps'
        },
        {
          icon: 'monetization_on',
          title: 'Lumpsum Investment Calculator',
          description: 'See how a one-time mutual fund investment grows into a maturity corpus over time.',
          route: '/lumpsum'
        },
        {
          icon: 'trending_up',
          title: 'SIP Calculator',
          description: 'See how a monthly mutual fund SIP grows into a maturity corpus over time.',
          route: '/sip'
        },
        {
          icon: 'stairs',
          title: 'Step-Up SIP Calculator',
          description: 'See how much extra corpus a yearly step-up in your SIP amount builds versus a flat SIP.',
          route: '/step-up-sip'
        },
        {
          icon: 'compare_arrows',
          title: 'SIP vs Step-Up SIP Calculator',
          description: 'Compare a flat SIP against a step-up SIP side by side, for the same starting amount and tenure.',
          route: '/sip-vs-step-up-sip'
        },
        {
          icon: 'swap_horiz',
          title: 'STP Calculator',
          description: 'Plan a Systematic Transfer Plan between a source and destination mutual fund.',
          route: '/stp'
        },
        {
          icon: 'call_made',
          title: 'SWP Calculator',
          description: 'Plan a Systematic Withdrawal Plan and see how long your corpus will last.',
          route: '/swp'
        },
        {
          icon: 'local_fire_department',
          title: 'FIRE Calculator',
          description: 'Find your FIRE number, the age you can retire early, and whether your corpus lasts.',
          route: '/fire'
        }
      ]
    },
    {
      label: 'Tax & Insurance Calculators',
      hint: 'Plan your protection and tax outgo with the same rigour you plan your investments.',
      calculators: [
        {
          icon: 'security',
          title: 'Term Insurance Calculator',
          description: 'Find out how much life cover you need based on your income, debts and future goals.',
          route: '/term-insurance'
        },
        {
          icon: 'receipt_long',
          title: 'Income Tax Calculator',
          description: 'Compare your tax liability under the Old and New regimes for FY 2025-26.',
          route: '/income-tax'
        },
        {
          icon: 'location_city',
          title: 'HRA Exemption Calculator',
          description: 'Calculate your House Rent Allowance tax exemption under Section 10(13A).',
          route: '/hra'
        }
      ]
    }
  ];
}
